import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Shield, FileText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function LegalShell({ icon: Icon, title, updated, tab, children }) {
  const tabs = [
    { to: "/privacy", label: "Privacy Policy", key: "privacy" },
    { to: "/terms", label: "Terms of Service", key: "terms" },
    { to: "/data-deletion", label: "Data Deletion", key: "data-deletion" },
  ];
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/icon-192.png" alt="FHAPAS" className="h-8 w-8 rounded-lg" />
            <span className="font-display text-lg font-extrabold text-slate-900">FHAPAS</span>
          </Link>
          <Link to="/"><Button variant="ghost" size="sm" className="gap-1.5 text-slate-600"><ArrowLeft className="h-4 w-4" /> Home</Button></Link>
        </div>
      </header>
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <div className="mb-6 flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {tabs.map((t) => (
            <Link key={t.key} to={t.to} data-testid={`legal-tab-${t.key}`}
              className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-teal-700 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {t.label}
            </Link>
          ))}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-teal-50 text-teal-700"><Icon className="h-5 w-5" /></div>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
              <p className="text-xs text-slate-400">Last updated {updated}</p>
            </div>
          </div>
          <div className="prose prose-slate mt-6 max-w-none text-sm leading-relaxed text-slate-600">{children}</div>
        </div>
      </div>
    </div>
  );
}

const H = ({ children }) => <h2 className="mt-6 font-display text-lg font-semibold text-slate-900">{children}</h2>;
const P = ({ children }) => <p className="mt-2">{children}</p>;

export function Privacy() {
  return (
    <LegalShell icon={Shield} title="Privacy Policy" updated="June 2026" tab="privacy">
      <P>Fendana Health (“we”, “us”) operates FHAPAS, the Fendana Hospital and Patient Administration System. This policy explains how we collect, use and protect personal and health information.</P>
      <H>Information we collect</H>
      <P>We process account details (name, email, role, organisation), and, where you are a patient, clinical and administrative records created by your healthcare provider — including appointments, diagnoses, prescriptions, lab and imaging results, and billing information.</P>
      <H>How we use information</H>
      <P>Data is used solely to deliver hospital and patient administration services to your organisation, to provide the patient portal, and to maintain security and audit trails. We do not sell personal data.</P>
      <H>Data protection</H>
      <P>Access is controlled through role-based permissions, encryption in transit, and a complete audit log. Each organisation’s data is logically isolated.</P>
      <H>Your rights</H>
      <P>You may request access, correction, or deletion of your data at any time via the <Link to="/data-deletion" className="text-teal-700 hover:underline">Data Deletion</Link> page or by contacting your organisation administrator.</P>
      <H>Contact</H>
      <P>Questions about privacy can be sent to privacy@fhapas.com.</P>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell icon={FileText} title="Terms of Service" updated="June 2026" tab="terms">
      <P>By registering an organisation or account on FHAPAS you agree to these Terms of Service.</P>
      <H>Use of the service</H>
      <P>FHAPAS is provided to licensed healthcare organisations and their authorised staff and patients. You are responsible for the accuracy of information entered and for maintaining the confidentiality of your credentials.</P>
      <H>Subscriptions</H>
      <P>FHAPAS is offered on Trial, Standard and Enterprise plans. Access to certain features may require an active paid subscription as configured for your organisation. Fees are billed in your organisation’s local currency.</P>
      <H>Acceptable use</H>
      <P>You must not misuse the platform, attempt unauthorised access, or use it in violation of applicable healthcare and data-protection regulations in your jurisdiction.</P>
      <H>Liability</H>
      <P>FHAPAS supports clinical operations but does not replace professional medical judgement. Fendana Health is not liable for clinical decisions made using the platform.</P>
      <H>Contact</H>
      <P>For questions about these terms, contact legal@fhapas.com.</P>
    </LegalShell>
  );
}

export function DataDeletion() {
  const [form, setForm] = useState({ email: "", org: "", reason: "" });
  const [done, setDone] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    setDone(true);
    toast.success("Deletion request submitted");
  };
  return (
    <LegalShell icon={Trash2} title="Data Deletion Request" updated="June 2026" tab="data-deletion">
      <P>You have the right to request deletion of your personal data held in FHAPAS. Submit the form below and your organisation’s data protection officer will process your request, typically within 30 days.</P>
      <P className="text-slate-500">Note: certain clinical records may be retained where required by law or professional regulation, even after account deletion.</P>
      {done ? (
        <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800" data-testid="deletion-success">
          <p className="font-semibold">Request received</p>
          <p className="mt-1 text-sm">We have logged your data deletion request for <b>{form.email}</b>. You will receive confirmation by email.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5"><Label>Account email</Label>
            <Input data-testid="deletion-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" /></div>
          <div className="space-y-1.5"><Label>Organisation (optional)</Label>
            <Input data-testid="deletion-org" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="Hospital / clinic name" /></div>
          <div className="space-y-1.5"><Label>Reason (optional)</Label>
            <Textarea data-testid="deletion-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} /></div>
          <Button data-testid="deletion-submit" type="submit" className="bg-rose-600 hover:bg-rose-700">Submit deletion request</Button>
        </form>
      )}
    </LegalShell>
  );
}
